<template>
  <div class="ai-summary-sections">
    <div class="mb-4">
      <p
        class="body2 font-weight-bold"
        v-html="$t('aiAssistant.securityGapsSummary.sections.overview')"
      />
    </div>

    <div v-for="(overviewItem, index) in content.overview" :key="index" class="mb-4">
      <div
        class="ai-summary-item-text"
        data-testid="ai-summary-item-text"
        v-html="parseMarkdown(overviewItem)"
      ></div>
    </div>

    <!-- Overview by module Section -->
    <div v-for="(moduleContent, moduleName) in content.modules" :key="moduleName" class="mb-4">
      <div
        class="body2 font-weight-bold mb-3"
        data-testid="ai-security-gap-section-title"
        v-html="getSecurityGapSectionTitle(moduleName)"
      ></div>

      <div
        class="mb-6 ai-security-gap-module"
        v-for="(moduleItem, index) in moduleContent"
        :key="index"
      >
        <div class="body3 font-weight-bold mb-2">
          {{ getSecurityGapTitleText(moduleItem) }}
        </div>

        <div
          class="ai-summary-item-text"
          data-testid="ai-summary-item-text"
          v-html="parseMarkdown(moduleItem.content)"
        ></div>

        <div
          v-if="moduleItem.actions?.length"
          class="ai-security-gap-actions d-flex justify-end ga-2 mt-3"
          data-testid="ai-security-gap-actions"
        >
          <v-btn
            v-for="action in reorderActions(moduleItem.actions)"
            :key="action"
            :variant="isIgnoreAction(action) ? 'outlined' : 'flat'"
            color="primary"
            rounded
            data-testid="ai-security-gap-action"
            @click="handleActionClick(action, moduleItem)"
          >
            {{ getActionButtonText(action) }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import type {
  SecurityGapsAiModuleContent,
  SecurityGapsOverviewContent,
} from "@/_store/ai-assistant.module";
import { parseMarkdown } from "@/_helpers/markdown";
import {
  SecurityGapAction,
  SecurityGapType,
  SecurityGapWidgetType,
} from "@/constants/security-gaps.ts";
import { AppContext } from "@/types.ts";

const securityGapTypeToEmojiMap: Record<SecurityGapWidgetType, string> = {
  [SecurityGapWidgetType.CLOUD_SECURITY]: "☁️",
  [SecurityGapWidgetType.ENDPOINT_SECURITY]: "💻",
  [SecurityGapWidgetType.EMAIL_SECURITY]: "📧",
  [SecurityGapWidgetType.USER_DATA_GOVERNANCE]: "👥",
  [SecurityGapWidgetType.ENDPOINT_DATA_GOVERNANCE]: "💾",
  [SecurityGapWidgetType.NETWORK]: "🌐",
  [SecurityGapWidgetType.DEVICES]: "🖥️",
  [SecurityGapWidgetType.USERS]: "👤",
  [SecurityGapWidgetType.MSP_WORKSPACE_LABELS]: "🏷️",
  [SecurityGapWidgetType.MANAGED_SERVICE]: "🎧",
};

export default defineComponent({
  props: {
    content: {
      type: Object as PropType<SecurityGapsOverviewContent>,
      required: true,
    },
    context: {
      type: String as PropType<AppContext>,
      required: true,
    },
  },
  emits: ["action-click"],
  setup(props, { emit }) {
    const { t, te } = useI18n();

    const getSecurityGapSectionTitle = (type: string) => {
      const emoji = securityGapTypeToEmojiMap[type as SecurityGapWidgetType] ?? "";
      return `${emoji} ${t(`securityGaps.types.${type}`)}:`;
    };

    const getSecurityGapBaseTitle = (misconfigurationType: SecurityGapType) => {
      const mspKey = `securityGaps.securityGapTypes.${misconfigurationType}.titleMsp`;
      const defaultKey = `securityGaps.securityGapTypes.${misconfigurationType}.title`;
      return props.context === AppContext.MSP && te(mspKey) ? t(mspKey) : t(defaultKey);
    };

    const getSecurityGapTitleText = (content: SecurityGapsAiModuleContent) => {
      const { misconfigurationType, affectedWorkspacesCount } = content;
      const isMsp = props.context === AppContext.MSP;

      const baseTitle = getSecurityGapBaseTitle(misconfigurationType);

      // Return a regular title if not MSP or no count provided
      if (!isMsp || !affectedWorkspacesCount) {
        return baseTitle;
      }

      const suffix = t(
        "aiAssistant.securityGapsSummary.acrossWorkspaces",
        {
          n: affectedWorkspacesCount,
        },
        affectedWorkspacesCount
      );

      return `${baseTitle} (${suffix})`;
    };

    const getActionButtonText = (action: SecurityGapAction) => {
      switch (action) {
        case SecurityGapAction.FIX_ALL:
        case SecurityGapAction.MANUAL_FIX:
          return t("securityGaps.actions.fixAll", 1);
        default:
          return t(`securityGaps.actions.${action}`);
      }
    };

    const getMessageText = (action: SecurityGapAction, misconfigurationType: SecurityGapType) => {
      const prefix = getActionButtonText(action);
      const title = getSecurityGapBaseTitle(misconfigurationType);
      return `${prefix} ${title}`.trim();
    };

    const handleActionClick = (action: SecurityGapAction, content: SecurityGapsAiModuleContent) => {
      const message = getMessageText(action, content.misconfigurationType);
      emit("action-click", message);
    };

    const isIgnoreAction = (action: SecurityGapAction) =>
      [SecurityGapAction.IGNORE, SecurityGapAction.UNIGNORE].includes(action);

    const reorderActions = (actions: SecurityGapAction[]) => [
      ...actions.filter(isIgnoreAction),
      ...actions.filter((action) => !isIgnoreAction(action)),
    ];

    return {
      parseMarkdown,
      getSecurityGapSectionTitle,
      getSecurityGapTitleText,
      getActionButtonText,
      handleActionClick,
      isIgnoreAction,
      reorderActions,
    };
  },
});
</script>

<style lang="scss" scoped>
.ai-summary-item-text {
  /* Allow long tokens (URLs, hashes) to wrap instead of forcing horizontal scroll. */
  overflow-wrap: break-word;
  /* Keep break-word for older engines that don't fully honor overflow-wrap. */
  word-break: break-word;

  :deep(p) {
    margin: 0;
  }

  :deep(strong) {
    font-weight: 600;
  }

  :deep(em) {
    font-style: italic;
  }

  :deep(code) {
    background-color: rgba(0, 0, 0, 0.1);
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }

  :deep(a) {
    font-family: "Source Sans Pro", sans-serif;
    font-size: 12px;
    color: rgb(var(--v-theme-link));
    text-decoration: underline;
    font-weight: 400;
  }

  :deep(ul),
  :deep(ol) {
    margin: 4px 0;
    padding-left: 16px;
  }

  :deep(li) {
    margin-bottom: 2px;
  }

  :deep(blockquote) {
    border-left: 3px solid rgb(var(--v-theme-gray-light));
    padding-left: 8px;
    margin: 4px 0;
    color: rgb(var(--v-theme-gray-medium));
    font-style: italic;
  }
}

.ai-security-gap-module {
  border: 2px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 8px;
  padding: 12px 16px;
}

.ai-security-gap-actions {
  flex-wrap: wrap;
}
</style>
